import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Collapse,
    IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { SmsService } from '../../../../service/smsService';

export default function BulkSmsResults({ batchId }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedLists, setExpandedLists] = useState({
        sent: false,
        blocked: false,
        failed: false
    });

    const fetchResults = async () => {
        try {
            setLoading(true);
            const response = await SmsService.getBatchResults(batchId, true);
            if (response?.code === 0) {
                setResults(response.data);
                setError('');
            } else {
                setError(response?.message || 'Failed to fetch results');
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch results');
            console.error('Results fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!batchId) return;

        const fetchResults = async () => {
            try {
                setLoading(true);
                const response = await SmsService.getBatchResults(batchId, true);
                if (response?.code === 0) {
                    setResults(response.data);
                    setError('');
                } else {
                    setError(response?.message || 'Failed to fetch results');
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch results');
                console.error('Results fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [batchId]);

    const toggleExpand = (key) => {
        setExpandedLists(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    if (loading && !results) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !results) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!results) {
        return <Alert severity="warning">No results available</Alert>;
    }

    /**
     * Render a collapsible section for sent/blocked/failed customers
     */
    const renderListSection = (title, key, list, color) => {
        if (!list || list.length === 0) return null;

        return (
            <Paper sx={{ mb: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: `${color}.50`,
                        cursor: 'pointer'
                    }}
                    onClick={() => toggleExpand(key)}
                >
                    <Box>
                        <Typography variant="subtitle2">{title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {list.length} customers
                        </Typography>
                    </Box>
                    <IconButton size="small">
                        {expandedLists[key] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>

                <Collapse in={expandedLists[key]}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell>Customer Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    {key === 'failed' && <TableCell>Error</TableCell>}
                                    {key === 'sent' && <TableCell>Sent At</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.map((item, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{item.customerName}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.status}
                                                size="small"
                                                color={
                                                    item.status === 'sent' ? 'success' :
                                                    item.status === 'blocked' ? 'warning' : 'error'
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        {key === 'failed' && (
                                            <TableCell>
                                                <Typography variant="caption" color="error">
                                                    {item.errorMessage}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        {key === 'sent' && (
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {item.sentAt ? new Date(item.sentAt).toLocaleString() : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Collapse>
            </Paper>
        );
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2">Detailed Results</Typography>

            {/* Sent SMS section */}
            {renderListSection(
                `Successfully Sent (${results.sentCount || 0})`,
                'sent',
                results.sent,
                'success'
            )}

            {/* Blocked SMS section */}
            {renderListSection(
                `Blocked - Opted Out (${results.blockedCount || 0})`,
                'blocked',
                results.blocked,
                'warning'
            )}

            {/* Failed SMS section */}
            {renderListSection(
                `Failed (${results.failedCount || 0})`,
                'failed',
                results.failed,
                'error'
            )}

            {/* Refresh button */}
            <Button
                size="small"
                onClick={fetchResults}
                disabled={loading}
            >
                Refresh Results
            </Button>
        </Stack>
    );
}
